import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { InputComponent } from '../../../../shared/input';
type Lang = 'english' | 'italian';

@Component({
  selector: 'app-category1-question7',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputComponent],
  template: `
    <form [formGroup]="form" class="flex flex-col gap-6">

      <!-- Types of fugitive gases -->
      <div>
        <div class="mb-2 font-medium">
          {{ strings[language].fg }}
        </div>

        <div *ngFor="let gas of fugitiveGasOptions">
          <label class="flex gap-2 items-center cursor-pointer text-[14px]">
            <input
              type="checkbox"
              class="w-4 h-4 accent-[#043B22]"
              [checked]="form.controls.fugitive_gases.value.includes(gas.value)"
              (change)="toggleArrayValue(form.controls.fugitive_gases, gas.value)"
            />
            {{ gas.label[language] }}
          </label>
        </div>
      </div>

      <!-- Sources -->
      <div>
        <div class="mb-2 font-medium">
          {{ strings[language].src }}
        </div>

        <div *ngFor="let src of sourceOptions">
          <label class="flex gap-2 items-center cursor-pointer text-[14px]">
            <input
              type="checkbox"
              class="w-4 h-4 accent-[#043B22]"
              [checked]="form.controls.emission_sources.value.includes(src.value)"
              (change)="toggleArrayValue(form.controls.emission_sources, src.value)"
            />
            {{ src.label[language] }}
          </label>
        </div>
      </div>

      <!-- Specific gases -->
      <div *ngIf="showGasDetails">
        <div class="font-medium mb-2">
          {{ strings[language].specific_gases }}
        </div>

        <div formArrayName="gases" class="flex flex-col gap-3">
          <div
            *ngFor="let row of gases.controls; let i = index"
            [formGroupName]="i"
            class="flex gap-3 items-end"
          >
            <app-input
              placeholder="{{ strings[language].gas_name }}"
              [control]="getFormControl(row, 'name')"
            ></app-input>

            <app-input
              type="number"
              unit="kg"
              placeholder="0"
              [control]="getFormControl(row, 'quantity')"
            ></app-input>

            <button
              type="button"
              class="text-red-600 text-sm"
              (click)="removeGas(i)"
            >
              ✕
            </button>
          </div>

          <button
            type="button"
            class="text-[#043B22] text-sm underline w-fit"
            (click)="addGas()"
          >
            {{ strings[language].add_gas }}
          </button>
        </div>
      </div>

      <!-- Localisation -->
      <div>
        <div class="mb-2">
          {{ strings[language].localisation }}
        </div>
        <app-input
          [control]="form.controls.localisation"
          placeholder="{{ strings[language].localisation }}"
        ></app-input>
      </div>

      <!-- Annual emissions -->
      <div>
        <div class="mb-2">
          {{ strings[language].annual_emissions }}
        </div>
        <app-input
          type="number"
          unit="kg CO₂e"
          placeholder="0"
          [control]="form.controls.annual_emissions"
        ></app-input>
      </div>

    </form>
  `
})
export class Category1Question7Component implements OnDestroy {
  private locale = inject(LocaleService);
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();
  language: Lang = this.locale.getLanguage();


  form = new FormGroup({
    fugitive_gases: new FormControl<string[]>([], { nonNullable: true }),
    emission_sources: new FormControl<string[]>([], { nonNullable: true }),
    gases: new FormArray<FormGroup>([]),
    localisation: new FormControl(''),
    annual_emissions: new FormControl<number | null>(null, Validators.required)
  });

  fugitiveGasOptions = [
    {
      value: 'ch4',
      label: { english: 'Methane (CH₄)', italian: 'Metano (CH₄)' }
    },
    {
      value: 'co2',
      label: {
        english: 'Carbon dioxide (CO₂)',
        italian: 'Anidride carbonica (CO₂)'
      }
    },
    {
      value: 'n2o',
      label: {
        english: 'Nitrous oxide (N₂O)',
        italian: 'Protossido di azoto (N₂O)'
      }
    },
    {
      value: 'refrigerant',
      label: {
        english: 'Refrigerant gases (HFC, HCFC)',
        italian: 'Gas refrigeranti (HFC, HCFC)'
      }
    },
    {
      value: 'other',
      label: {
        english: 'Other gases (specify)',
        italian: 'Altri gas (specificare)'
      }
    }
  ];

  sourceOptions = [
    {
      value: 'refrigeration',
      label: {
        english: 'Refrigeration systems',
        italian: 'Sistemi di refrigerazione'
      }
    },
    {
      value: 'pipes',
      label: {
        english: 'Pipes and valves',
        italian: 'Tubazioni e valvole'
      }
    },
    {
      value: 'ventilation',
      label: {
        english: 'Ventilation systems',
        italian: 'Sistemi di ventilazione'
      }
    },
    {
      value: 'industrial',
      label: {
        english: 'Industrial processes',
        italian: 'Processi industriali'
      }
    }
  ];

  strings: Record<Lang, any> = {
    english: {
      fg: 'Types of fugitive gases emitted',
      src: 'Sources of fugitive emissions',
      localisation: 'Specify where leaks occur',
      annual_emissions: 'Total annual emissions',
      specific_gases: 'Specific gases and leaked quantities',
      gas_name: 'Gas name (e.g. R410A)',
      add_gas: '+ Add gas'
    },
    italian: {
      fg: 'Tipi di gas fuggitivi emessi',
      src: 'Fonti delle emissioni fuggitive',
      localisation: 'Specificare dove avvengono le perdite',
      annual_emissions: 'Emissioni annuali totali',
      specific_gases: 'Gas specifici e quantità rilasciate',
      gas_name: 'Nome del gas (es. R410A)',
      add_gas: '+ Aggiungi gas'
    }
  };

  get gases() {
    return this.form.controls.gases;
  }

  get showGasDetails(): boolean {
    const values = this.form.controls.fugitive_gases.value;
    return values.includes('refrigerant') || values.includes('other');
  }

  addGas() {
    this.gases.push(
      new FormGroup({
        name: new FormControl('', Validators.required),
        quantity: new FormControl<number | null>(null, Validators.required)
      })
    );
  }

  removeGas(index: number) {
    this.gases.removeAt(index);
  }

  toggleArrayValue(control: FormControl<string[]>, value: string) {
    const arr = [...control.value];
    const idx = arr.indexOf(value);
    idx >= 0 ? arr.splice(idx, 1) : arr.push(value);
    control.setValue(arr);
  }

  getFormControl(group: FormGroup, name: string): FormControl {
    return group.get(name) as FormControl;
  }

  ngOnInit() {
    const stored = localStorage.getItem('categories');
    if (!stored) return;

    const parsed = JSON.parse(stored);
    const answers = parsed?.[0]?.questions?.[6]?.answers;
    if (!answers) return;
    this.gases.clear();
    if (Array.isArray(answers.gases)) {
      answers.gases.forEach((g: any) => {
        this.gases.push(
          new FormGroup({
            name: new FormControl(g.name),
            quantity: new FormControl(g.quantity)
          })
        );
      });
    }
    this.form.patchValue({
      fugitive_gases: answers.fugitive_gases ?? [],
      emission_sources: answers.emission_sources ?? [],
      localisation: answers.localisation ?? '',
      annual_emissions: answers.annual_emissions ?? null
    });
    this.form.valueChanges.subscribe(() => {
      this.categories[0].questions[6].answers = this.form.value as any;
      this.categoryService.persistCategories(this.categories);
    });
  }

  ngOnDestroy(): void {
    this.categories[0].questions[6].answers = this.form.value as any;
    this.categoryService.persistCategories(this.categories);
  }


}
